/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { AuthContext } from "context";
// MUI Core Components
import {
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Tooltip,
  Button,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";

// Settings page components
import CrudService from "services/cruds-service";
import { useState, useEffect, useContext } from "react";
import { useMaterialUIController, setOpenChatBot } from "context";
import AssessmentComponent from "./AssessmentComponent";
import useQuizState from "./useQuizState";
// Custom Components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";

function Assessment({
  courseId,
  courseName
}) {
  const [controller, dispatch] = useMaterialUIController();
  useEffect(() => {
    setOpenChatBot(dispatch, false);
  }, []);
  const [user, setUser] = useState("");
  const { setIsAuthenticated, getCurrentUser } = useContext(AuthContext);

  const [initialResponse, setInitialResponse] = useState({});
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [level, setLevel] = useState("explore");
  const [learningLevels, setLearningLevels] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState({ isloading: false, message: "" });
  const [detailResponse, setDetailResponse] = useState({
    user_name: "",
    course_name: "",
    course_description: "",
    base64_data: "",
    course_modules_list: [],
  });
  const {
    quizLoader,
    setQuizLoader,
    currentQuestion,
    setCurrentQuestion,
    quizCertificate,
    setQuizCertificate,
    quizResults,
    setQuizResults,
    quizSubmitted,
    setQuizSubmitted,
    answeredQuestions,
    setAnsweredQuestions,
    performanceByModule,
    setPerformanceByModule,
    performance_data,
    setPerformanceData,
  } = useQuizState();

  const [selectedLearningLevel, setSelectedLearningLevel] = useState([]); // State to track selected learning level

  useEffect(() => {
    const start_time = Date.now();
    async function checkToken() {
      try {
        let user_id = await getCurrentUser();
        if (user_id === "") {
          setIsAuthenticated(false);
          localStorage.removeItem("token");
        }
        setUser(user_id);
        analytics.track(events.VIEW_PAGE, { page: "assessment", userId: user_id, courseId: courseId });
        return user_id;
      } catch (error) {
        console.error("Failed to get user id", error);
        alert("Something went wrong. Please try again later.");
      }
    }

    async function getQuizCertificate(course_id, user_id) {
      try {
        let certificateData = await CrudService.getShowCertificate(courseId, user_id, "explore"); // Pass both userId and courseId
        const response = await CrudService.getQuizCertificate(
          course_id,
          user_id,
          level
        );
        setDetailResponse(certificateData);
        if (response) {
          setQuizCertificate(`${response}`);
          setQuizSubmitted(true);
        }
      } catch (error) {
        console.error("Failed to get quiz certificate", error);
      }
    }
    checkToken().then((user_id) => {
      setLoading({ isloading: true, message: "Loading assessment..." });
      setUser(user_id);
      getQuizCertificate(courseId, user_id, level);
      setLoading({ isloading: false, message: "" });
    });
    return () => {
      checkToken().then((user_id) => {
      analytics.track(events.PAGE_LEFT, { page: "assessment", userId: user_id, courseId: courseId, start_time: start_time, end_time: Date.now() });
      });
    }
  }, []);

  useEffect(() => {
    async function getQuizQuestions(courseId, user_id, level) {
      setLoading({ isloading: true, message: "Loading assessment..." });
      try {
        if (user_id === "") return; // Ensure user_id is available before making the request
        // Fetching the questions and other data based on the level
        const response = await CrudService.postUserQuestionsByLevel(courseId, user_id, level);
        setInitialResponse(response); // Store the initial response for later use
        const questions = response.questions;
        const assessment_level = response.assessment_level;
        const learning_levels = response.course_learning_level;

        setQuizQuestions(questions);
        setLevel(assessment_level);
        setLearningLevels(learning_levels);

        // Find the learning level object whose name matches assessment_level
        const matchedLevel = learning_levels.find(level => level.name === assessment_level);
        setSelectedLearningLevel(matchedLevel); // Set the selected learning level
        setCompleted(matchedLevel.completed);

        if (matchedLevel.completed) {
          const lastObject = matchedLevel.attempts[matchedLevel.attempts.length - 1];
          setAnsweredQuestions(lastObject.quizAnswers);
        }
        else {
          // If no match is found, set completed to false and set answered questions
          setCompleted(false);
          setAnsweredQuestions(questions);
        }
        setQuizLoader(false);
        setQuizSubmitted(false);
        setQuizResults({});
        setPerformanceByModule([]);
        setPerformanceData({});
        setQuizCertificate(null);
        setCurrentQuestion(0); // Reset current question index to 0
      } catch (error) {
        console.error("Failed to get quiz questions", error);
      }
      finally {
        setLoading({ isloading: false, message: "" });
      }
    }

    if (user !== "") { // Ensure user_id exists before calling the function
      getQuizQuestions(courseId, user, level);
    }
  }, [courseId, user, level]); // Add user as a dependency

  function calculatePerformanceByModule(questions) {
    const performanceByModules = {};

    for (let i = 0; i < questions.length; i++) {
      const module = questions[i].module;
      const score = questions[i].user_answer === questions[i].answer_option ? 1 : 0;

      if (performanceByModules[module]) {
        performanceByModules[module].score += score;
        performanceByModules[module].total += 1;
      } else {
        performanceByModules[module] = { score, total: 1 };
      }
    }

    return Object.keys(performanceByModules).map((module) => {
      const { score, total } = performanceByModules[module];
      return { module, score, total };
    });
  }


  const submitQuizResults = async () => {
    let currentCompletedStatus = selectedLearningLevel?.completed || false;
    setQuizLoader(true);
    setQuizSubmitted(true);

    try {
      const response = await CrudService.submitQuiz({
        course_id: courseId,
        user_id: user,
        answered_questions: answeredQuestions,
        level: level,
      });

      analytics.track(events.ASSESSMENT_SUBMITTED, { courseId: courseId, userId: user, level: level });

      setQuizResults(response);

      const certificateObject = {
        passed: response.passed,
        score: response.score,
        link: response.certificate,
        quizAnswers: response.questions,
      };

      if (response.passed) {
        currentCompletedStatus = true;
        analytics.track(events.ASSESSMENT_COMPLETED, { courseId: courseId, userId: user, level: level });
      }

      // Create a new certificates array
      const updatedAttempts = [
        ...(selectedLearningLevel?.attempts || []),
        certificateObject,
      ];

      const newLevel = {
        name: level,
        completed: currentCompletedStatus,
        attempts: updatedAttempts,
      };

      setSelectedLearningLevel(newLevel);
      setCompleted(currentCompletedStatus);

      const performanceByModule = calculatePerformanceByModule(response.questions);
      setPerformanceByModule(performanceByModule);

      const performanceData = {
        columns: [
          { Header: "Module", accessor: "module" },
          { Header: "Score", accessor: "score" },
          { Header: "Total", accessor: "total" },
        ],
        rows: performanceByModule,
      };
      setPerformanceData(performanceData);
      setQuizQuestions(response.questions);
    } catch (error) {
      console.error("Failed to submit quiz results", error);
    } finally {
      setQuizLoader(false);
    }
  };


  const handleLevelChange = async (newLevel) => {
    setLevel(newLevel);
    analytics.track(events.ASSESSMENT_STARTED, { courseId: courseId, userId: user, level: newLevel });
    // Now ideally useEffect should run and reset everything. 
  };

  // Show loading spinner while fetching courses
  if (loading.isloading) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress color="info" />
        <MDTypography variant="h4" style={{ marginLeft: 16 }}>
          {loading.message}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <>

      <AssessmentComponent
        initialResponse={initialResponse}
        selectedLearningLevel={selectedLearningLevel}
        courseName={courseName}
        calculatePerformanceByModule={calculatePerformanceByModule}
        quizLoader={quizLoader}
        quizQuestions={quizQuestions}
        setQuizQuestions={setQuizQuestions}
        currentQuestion={currentQuestion}
        setCurrentQuestion={setCurrentQuestion}
        quizCertificate={quizCertificate}
        quizResults={quizResults}
        setQuizResults={setQuizResults}
        quizSubmitted={quizSubmitted}
        setQuizSubmitted={setQuizSubmitted}
        answeredQuestions={answeredQuestions}
        setAnsweredQuestions={setAnsweredQuestions}
        submitQuizResults={submitQuizResults}
        performanceByModule={performanceByModule}
        setPerformanceByModule={setPerformanceByModule}
        performance_data={performance_data}
        setPerformanceData={setPerformanceData}
        detailResponse={detailResponse}
        user={user}
        courseId={courseId}
        level={level}
        learningLevels={learningLevels}
        handleLevelChange={handleLevelChange}
        showName={true}
        showPerformanceData={true}
        completed={completed}
        setCompleted={setCompleted}
      />

    </>
  );
}

export default Assessment;
